import { IssueCheckError, JsmInvocation } from '@app/booking-types/types';
import React, { useEffect, useRef, useState } from 'react';
import { isStateValid } from '../shared';
import { Box, ErrorMessage, Select, Spinner, useProductContext } from '@forge/react';
import { useUserContext } from '../../libs/useUserContext';
import { invoke } from '@forge/bridge';
import { Logger } from '@app/booking-libs';
import { SuggestionsBox } from './SuggestionsBox';
import { ConflictCheckBox } from './ConflictReportBox';
import { SelectResource } from './SelectResource';
import { SelectSuggestion } from './SelectSuggestion';
import { ConflictCheck } from './ConflictCheck';
function hasInvalidatingChanges(a, b) {
    return a.endDate !== b.endDate || a.startDate !== b.startDate || a.resource !== b.resource;
}
const log = new Logger('JsmResourceSelection');
/**
 * This component serves as a wrapper as anonymous users cannot use the Jira
 * API from the browser. At some point, we'll integrate another SelectSuggestion
 * that will be able to use invocations.
 *
 * @param props
 * @constructor
 */
export function JsmResourceSelection(props) {
    const { configuration, state, onSelect } = props;
    const { resource, startDate, endDate } = state;
    const [loading, setLoading] = useState(false);
    const isValid = isStateValid(state, false);
    const previousValues = useRef({});
    const c = useProductContext();
    const user = useUserContext();
    const [resources, setResources] = useState();
    // biome-ignore lint/correctness/useExhaustiveDependencies: not interested in other props
    useEffect(() => {
        function load() {
            if (!user.unlicensed) {
                log.debug('Jira user, using API from browser');
                return;
            }
            if (!hasInvalidatingChanges(state, previousValues.current)) {
                log.debug('No interesting changes in state, ignoring');
                return;
            }
            setLoading(true);
            previousValues.current = JSON.parse(JSON.stringify(state));
            const { resourceFieldId, issuetypeId, projectId } = props.configuration;
            const { startDate, endDate, resource } = state;
            const payload = {
                resourceFieldId,
                issuetypeId,
                projectId,
                startDate,
                endDate,
                resource
            };
            invoke(JsmInvocation.getResources, payload).then((result) => {
                if (result.success) {
                    setResources(result.payload);
                }
                setLoading(false);
            });
        }
        load();
    }, [state]);
    if (!user.unlicensed) {
        return (React.createElement(React.Fragment, null,
            React.createElement(SelectResource, { value: resource, config: configuration, onChange: (forgeValue) => onSelect(forgeValue.value) }),
            React.createElement(SelectSuggestion, { configuration: configuration, resource: resource, userStartTime: startDate, userEndTime: endDate, onSelect: onSelect }),
            isValid && React.createElement(ConflictCheck, { values: state, configuration: configuration, isValid: isValid })));
    }
    if (loading) {
        return React.createElement(Spinner, { size: 'medium' });
    }
    if (!resources || (resources === null || resources === void 0 ? void 0 : resources.allAvailableResources.length) === 0) {
        return React.createElement(ErrorMessage, null, "No available options, check Booking Flow configuration.");
    }
    const selectedValue = resources ? resources.allAvailableResources.find((o) => o.value === resource) : undefined;
    return (React.createElement(Box, null,
        React.createElement(Select, { isLoading: loading, value: selectedValue, options: (resources === null || resources === void 0 ? void 0 : resources.allAvailableResources) || [], onChange: (v) => onSelect(v.value) }),
        resources && (React.createElement(React.Fragment, null,
            React.createElement(SuggestionsBox, { hasSelection: Boolean(resource), suggestions: resources.suggestions, onSelect: onSelect }),
            isValid && resources.conflictCheckResults && (React.createElement(Box, { paddingBlockStart: 'space.100' },
                React.createElement(ConflictCheckBox, { selectedLabel: selectedValue.label, hasConflict: resources.conflictCheckResults.result === IssueCheckError.CONFLICT, conflictingIssues: [] })))))));
}
